// Tag-based React Query keys.
//
// Why: every tenant-scoped query MUST embed the current tenant slug so that
// switching tenants produces a fresh cache entry per tenant and the query
// refetches naturally. Otherwise React Query keeps serving the previous
// tenant's cached data — that bug shipped twice before this rewrite (see
// PR #45, #46 history).
//
// Convention: tenant-scoped helpers take ``tenant`` as the first arg.
// Tenant-agnostic queries (``me``, ``tenants``, ``monday``) stay flat.
// Always read keys through these helpers so a rename here propagates
// everywhere.

export const queryKeys = {
  // Tenant-agnostic — never scoped by tenant slug.
  me: () => ['me'] as const,
  myPreferences: () => ['me', 'preferences'] as const,
  tenants: () => ['tenants'] as const,
  allTenants: () => ['tenants', 'all'] as const,
  users: () => ['users'] as const,
  accessGroups: () => ['access-groups'] as const,
  entities: () => ['entities'] as const,
  monday: () => ['monday'] as const,
  // The board is per tenant (the API answers for X-Tenant-Slug). No tenant →
  // the prefix, for invalidating every tenant's board at once.
  mondayBoard: (tenant?: string) =>
    tenant ? (['monday', 'board', tenant] as const) : (['monday', 'board'] as const),
  mondayUpdates: (itemId: string) => ['monday', 'updates', itemId] as const,

  // Group (Alkemy+) — spans all tenants; not scoped by slug.
  group: (fy: number, snapshot: string | null = null) =>
    ['group', fy, snapshot ?? ''] as const,
  groupOverview: (gs: string, fy: number, filtersWithFY: Record<string, unknown> = {}) =>
    ['group', gs, 'overview', fy, filtersWithFY] as const,
  groupIndustryAnalysis: (gs: string, fy: number, filtersWithFY: Record<string, unknown>) =>
    ['group', gs, 'industry-analysis', fy, filtersWithFY] as const,
  groupClientBreakdown: (gs: string, fy: number, filtersWithFY: Record<string, unknown>) =>
    ['group', gs, 'client-breakdown', fy, filtersWithFY] as const,
  groupScorecard: (gs: string, fy: number, filtersWithFY: Record<string, unknown>) =>
    ['group', gs, 'scorecard', fy, filtersWithFY] as const,
  // Filter option lists unioned across the entity's tenants — independent of
  // the active filters, scoped only by the entity slug.
  groupFilterOptions: (gs: string) => ['group', gs, 'filter-options'] as const,
  // Practice views across the entity (Pipeline, Client Analysis) and their
  // row drill-downs.
  groupPipelineAnalysis: (gs: string, fy: number) =>
    ['group', gs, 'pipeline-analysis', fy] as const,
  groupClientsAnalysis: (gs: string, request: Record<string, unknown>) =>
    ['group', gs, 'clients-analysis', request] as const,
  groupHrWarnings: (gs: string, fy: number) => ['group', gs, 'hr-warnings', fy] as const,
  groupTimesheetDetail: (gs: string, targets: string, project: string | null, fy: number) =>
    ['group', gs, 'timesheet-detail', targets, project ?? '__client__', fy] as const,
  // Feedback list is admin-only and returns rows across all tenants
  // (see api/app/api/v1/feedback/routes.py::list_feedback) — not scoped.
  feedback: (status: string | null = null, type: string | null = null) =>
    ['feedback', status ?? '', type ?? ''] as const,

  // Tenant-scoped — slug first so prefix-based invalidation by tenant works.
  dashboardSnapshot: (tenant: string, filtersWithFY: Record<string, unknown>) =>
    ['dashboard', 'snapshot', tenant, filtersWithFY] as const,
  budgetDelta: (tenant: string, filtersWithFY: Record<string, unknown>) =>
    ['budget-delta', tenant, filtersWithFY] as const,
  budgetDeltaIndustry: (tenant: string, filtersWithFY: Record<string, unknown>) =>
    ['budget-delta-industry', tenant, filtersWithFY] as const,
  // Every upload of the tenant, all fiscal years — see hooks/useUploads.ts.
  uploadsAll: (tenant: string) => ['uploads', tenant] as const,
  clients: (tenant: string) => ['clients', tenant] as const,
  projectManagers: (tenant: string) => ['clients', 'project-managers', tenant] as const,
  accountOwners: (tenant: string) => ['clients', 'account-owners', tenant] as const,
  markets: (tenant: string) => ['clients', 'markets', tenant] as const,
  industryLeaders: (tenant: string) => ['clients', 'industry-leaders', tenant] as const,
  sbus: (tenant: string) => ['clients', 'sbus', tenant] as const,
  sbusHr: (tenant: string) => ['clients', 'sbus-hr', tenant] as const,
  pipelineOptions: (tenant: string) =>
    ['clients', 'pipeline-options', tenant] as const,
  clientsAnalysis: (tenant: string, request: Record<string, unknown>) =>
    ['clients', 'analysis', tenant, request] as const,
  pipelineAnalysis: (tenant: string, fy: number, withMedia = false) =>
    ['pipeline', 'analysis', tenant, fy, withMedia] as const,
  timesheetDetail: (tenant: string, client: string, project: string | null, fy: number) =>
    ['timesheet-detail', tenant, client, project ?? '__client__', fy] as const,
  reports: (tenant: string) => ['reports', tenant] as const,
  // Budget planner: the shared plan (settings + prior-year rows) and its
  // live base-year inputs (latest backlog + open pipeline).
  budgetPlan: (tenant: string, year: number) => ['budget-plan', tenant, year] as const,
  budgetPlanSource: (tenant: string, year: number) =>
    ['budget-plan', tenant, year, 'source'] as const,
  pulse: (tenant: string, fy: number, search = '') => ['pulse', tenant, fy, search] as const,
  alerts: (tenant: string, fy: number) => ['alerts', tenant, fy] as const,
  deltaWow: (
    tenant: string,
    from: string,
    to: string,
    includeUnchanged: boolean,
    filters: Record<string, unknown>,
  ) => ['delta-wow', tenant, from, to, includeUnchanged, filters] as const,
  deltaWowPipeline: (
    tenant: string,
    from: string,
    to: string,
    includeUnchanged: boolean,
    filters: Record<string, unknown>,
  ) =>
    ['delta-wow-pipeline', tenant, from, to, includeUnchanged, filters] as const,
  hrMonthly: (tenant: string, fy: number) => ['hr-monthly', tenant, fy] as const,
  invoicesMatching: (tenant: string, filters: Record<string, unknown>) =>
    ['invoices', 'matching', tenant, filters] as const,
  settingsSal: (tenant: string, fy: number) => ['settings', 'sal', tenant, fy] as const,
  settingsPortfolios: (tenant: string, fy: number) =>
    ['settings', 'portfolios', tenant, fy] as const,
  settingsDashboard: (tenant: string, fy: number) =>
    ['settings', 'dashboard', tenant, fy] as const,
  clientsProducts: (tenant: string) => ['clients', 'products', tenant] as const,
  level2Portfolios: (tenant: string) => ['level2-portfolios', tenant] as const,
} as const;
